import { createContext, useContext, useEffect, useState } from 'react'
import { authStore } from './authStore'

const API_URL = import.meta.env.VITE_API_URL as string

const AuthContext = createContext<{
  isAuthenticated: boolean
  logout: () => void
  refreshAccessToken: () => Promise<void>
} | null>(null)

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(!!authStore.getAccessToken())

  const logout = () => {
    authStore.clear()
    setIsAuthenticated(false)
  }

  const refreshAccessToken = async () => {
    const refresh = authStore.getRefreshToken()
    if (!refresh) return logout()

    try {
      const res = await fetch(API_URL + '/auth/refresh', {
        method: 'POST',
        body: JSON.stringify({ refresh_token: refresh }),
        headers: { 'Content-Type': 'application/json' },
      })

      if (!res.ok) throw new Error('Refresh failed')
      const data = await res.json()
      authStore.setTokens(data.access_token, data.refresh_token)
      setIsAuthenticated(true)
    } catch (err) {
      logout()
    }
  }

  useEffect(() => {
    const token = authStore.getAccessToken()
    if (!token) return

    const interval = setInterval(refreshAccessToken, 60 * 60 * 1000) // every 1 hour
    return () => clearInterval(interval)
  }, [])

  return (
    <AuthContext.Provider value={{ isAuthenticated, logout, refreshAccessToken }}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
